import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TableHead,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Payment as PaymentIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Today as TodayIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import axios from "axios";
import { auth } from "../../firebase-config";

const Payments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonUsers, setLessonUsers] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterBy, setFilterBy] = useState('payment');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  // "<userId>:<payment|attendance>" while its PATCH is in flight
  const [busyKey, setBusyKey] = useState(null);
  // user waiting for "undo payment" confirmation
  const [undoPaymentUser, setUndoPaymentUser] = useState(null);

  useEffect(() => {
    fetchLessons();
  }, [selectedDate]);

  const fetchLessons = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }
      const idToken = await user.getIdToken();
      
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/payment/history/layout`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      setLessons(response.data.payments);
    } catch (error) {
      console.error("Failed to fetch lessons:", error);
      setSnackbar({
        open: true,
        message: error.message === "User not authenticated" 
          ? 'Musisz być zalogowany aby zobaczyć tę stronę'
          : 'Błąd podczas ładowania danych o płatnościach',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Open dialog for selected lesson
  const handleLessonClick = (lesson) => {
    setSelectedLesson(lesson);
    setLessonUsers({
      payed: lesson.payed,
      not_payed: lesson.not_payed,
      attended: lesson.attended,
      not_attended: lesson.not_attended,
    });
    setDialogOpen(true);
  };

  const handleFilterChange = (_, newFilter) => {
    if (newFilter !== null) {
      setFilterBy(newFilter);
    }
  };

  const updateUserStatus = async (userId, lessonId, statusType, newValue) => {
    const key = `${userId}:${statusType}`;
    if (busyKey) return;
    setBusyKey(key);
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }
      const idToken = await user.getIdToken();
      
      const endpoint = statusType === 'payment' 
        ? `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson/${lessonId}/users/${userId}/payment`
        : `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson/${lessonId}/users/${userId}/attendance`;
      
      const payload = statusType === 'payment'
        ? { payed: newValue }
        : { attended: newValue };

      await axios.patch(endpoint, payload, {
        headers: { Authorization: `Bearer ${idToken}` }
      });

      // Refresh lesson data to get updated categorization
      const refreshResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/payment/history/layout`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      setLessons(refreshResponse.data.payments);

      const updatedLesson = refreshResponse.data.payments.find(l => l.lesson_id === selectedLesson.lesson_id);
      if (updatedLesson) {
        setLessonUsers({
          payed: updatedLesson.payed,
          not_payed: updatedLesson.not_payed,
          attended: updatedLesson.attended,
          not_attended: updatedLesson.not_attended,
        });
      }

      setSnackbar({
        open: true,
        message: `Status użytkownika został zaktualizowany`,
        severity: 'success'
      });

    } catch (error) {
      console.error('Failed to update user status:', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || `Błąd podczas aktualizacji statusu`,
        severity: 'error'
      });
    } finally {
      setBusyKey(null);
    }
  };

  const confirmUndoPayment = () => {
    const user = undoPaymentUser;
    setUndoPaymentUser(null);
    updateUserStatus(user.id, selectedLesson.lesson_id, 'payment', false);
  };

  const getUserCurrentStatus = (user, statusType) => {
    if (statusType === 'payment') {
      // Check if user is in payed or not_payed list
      const isPayed = lessonUsers.payed.some(u => u.id === user.id);
      return isPayed ? 'paid' : 'not_paid';
    } else {
      // Check if user is in attended or not_attended list
      const isAttended = lessonUsers.attended.some(u => u.id === user.id);
      const isNotAttended = lessonUsers.not_attended.some(u => u.id === user.id);
      if (isAttended) return 'attended';
      if (isNotAttended) return 'not_attended';
      return 'unknown';
    }
  };

  const getAllUsers = () => {
    if (!lessonUsers) return [];
    
    // Combine all users and remove duplicates
    const allUsers = [
      ...lessonUsers.payed,
      ...lessonUsers.not_payed,
      ...lessonUsers.attended,
      ...lessonUsers.not_attended
    ];
    
    // Remove duplicates based on user ID
    const uniqueUsers = allUsers.filter((user, index, self) => 
      index === self.findIndex(u => u.id === user.id)
    );
    
    return uniqueUsers;
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLesson(null);
    setLessonUsers(null);
  };

  const formatLessonDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalUsers = (lesson) => {
    const paidCount = lesson.payed?.length || 0;
    const unpaidCount = lesson.not_payed?.length || 0;
    return paidCount + unpaidCount;
  };

  const getPaidCount = (lesson) => {
    return lesson.payed?.length || 0;
  };

  const getAttendedCount = (lesson) => {
    return lesson.attended?.length || 0;
  };

  const navigateToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const navigateToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const formatSelectedDate = () => {
    return selectedDate.toLocaleDateString('pl-PL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const isToday = () => {
    const today = new Date();
    return selectedDate.toDateString() === today.toDateString();
  };

  // Filter lessons by selected date
  const filteredLessons = lessons.filter(lesson => {
    const lessonDate = new Date(lesson.lesson_start);
    return lessonDate.toDateString() === selectedDate.toDateString();
  });

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography 
          variant="h5" 
          component="h2" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          Zarządzanie Płatnościami
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Przeglądaj i zarządzaj płatnościami za zajęcia oraz obecnością uczestników
        </Typography>
      </Box>

      {/* Date Navigation Bar - Flutter Style */}
      <Paper 
        elevation={2} 
        sx={{ 
          mb: 3, 
          p: { xs: 1.5, sm: 2 },
          backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#f5f5f5'
        }}
      >
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          {/* Left: Previous Day Button */}
          <IconButton
            onClick={navigateToPreviousDay}
            sx={{ 
              color: 'primary.main',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
            aria-label="poprzedni dzień"
          >
            <ArrowBackIosIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>

          {/* Center: Date Display (clickable to go to today) */}
          <Box 
            onClick={goToToday}
            sx={{ 
              flex: 1,
              textAlign: 'center',
              cursor: 'pointer',
              px: 2,
              py: 1,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                fontWeight: 600,
                color: isToday() ? 'primary.main' : 'text.primary'
              }}
            >
              {isToday() ? 'Dzisiejsze Zajęcia' : 'Zajęcia'}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'text.secondary'
              }}
            >
              {formatSelectedDate()}
            </Typography>
          </Box>

          {/* Right: Next Day, Today, and Refresh Buttons */}
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              onClick={navigateToNextDay}
              sx={{ 
                color: 'primary.main',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
              aria-label="następny dzień"
            >
              <ArrowForwardIosIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
            
            <IconButton
              onClick={goToToday}
              sx={{ 
                color: 'primary.main',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
              aria-label="idź do dziś"
              title="Idź do dziś"
            >
              <TodayIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
            
            <IconButton
              onClick={fetchLessons}
              sx={{ 
                color: 'primary.main',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
              aria-label="odśwież"
              title="Odśwież"
            >
              <RefreshIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Box textAlign="center">
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">
              Ładowanie danych o płatnościach...
            </Typography>
          </Box>
        </Box>
      ) : filteredLessons.length > 0 ? (
        <Box sx={{ 
          display: 'grid', 
          gap: 2, 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(auto-fit, minmax(300px, 1fr))',
            md: 'repeat(auto-fill, minmax(350px, 1fr))'
          }
        }}>
          {filteredLessons.map((lesson) => {
            const totalUsers = getTotalUsers(lesson);
            const paidUsers = getPaidCount(lesson);
            const paymentPercentage = totalUsers > 0 ? (paidUsers / totalUsers) * 100 : 0;

            return (
              <Card 
                key={lesson.lesson_id}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                  }
                }}
                onClick={() => handleLessonClick(lesson)}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  {/* Lesson Title with Icon */}
                  <Box display="flex" alignItems="center" mb={1}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: '#667eea',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5
                      }}
                    >
                      <PaymentIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                    </Box>
                    <Box flex={1}>
                      <Typography 
                        variant="h6" 
                        component="div"
                        sx={{ 
                          fontSize: { xs: '0.95rem', sm: '1.1rem' },
                          fontWeight: 'bold',
                          lineHeight: 1.2
                        }}
                      >
                        {lesson.lesson_name}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Time and Trainer Info */}
                  <Box sx={{ mb: 2, ml: 6.5 }}>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          color: 'text.secondary'
                        }}
                      >
                        🕐 {new Date(lesson.lesson_start).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          color: 'text.secondary'
                        }}
                      >
                        👤 {lesson.trainer_name || 'Brak trenera'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Divider */}
                  <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

                  {/* Statistics Row - Flutter Style */}
                  <Box 
                    display="flex" 
                    justifyContent="space-around"
                    alignItems="center"
                  >
                    {/* Total Students */}
                    <Box textAlign="center">
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 'bold',
                          fontSize: { xs: '1.1rem', sm: '1.3rem' }
                        }}
                      >
                        {totalUsers}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary',
                          fontSize: { xs: '0.65rem', sm: '0.75rem' }
                        }}
                      >
                        Uczestnicy
                      </Typography>
                    </Box>

                    {/* Paid Count */}
                    <Box textAlign="center">
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 'bold',
                          color: '#4caf50',
                          fontSize: { xs: '1.1rem', sm: '1.3rem' }
                        }}
                      >
                        {paidUsers}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary',
                          fontSize: { xs: '0.65rem', sm: '0.75rem' }
                        }}
                      >
                        Zapłacone
                      </Typography>
                    </Box>

                    {/* Attended Count */}
                    <Box textAlign="center">
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 'bold',
                          color: '#2196f3',
                          fontSize: { xs: '1.1rem', sm: '1.3rem' }
                        }}
                      >
                        {getAttendedCount(lesson)}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary',
                          fontSize: { xs: '0.65rem', sm: '0.75rem' }
                        }}
                      >
                        Obecni
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PaymentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Brak danych o płatnościach
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nie znaleziono żadnych zajęć z danymi o płatnościach
          </Typography>
        </Paper>
      )}

      {/* Dialog for users in a selected lesson */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
          <Box display="flex" alignItems="center" gap={1}>
            <PaymentIcon color="primary" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            <Box>
              <Typography variant="h6" component="div" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {selectedLesson?.lesson_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {selectedLesson?.lesson_start && formatLessonDate(selectedLesson.lesson_start)}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Summary Card - Flutter Style */}
          {lessonUsers && (
            <Card elevation={2} sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box 
                  display="flex" 
                  justifyContent="space-around"
                  alignItems="center"
                >
                  {/* Total Students */}
                  <Box textAlign="center">
                    <PeopleIcon sx={{ color: 'text.secondary', fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 0.5 }} />
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.3rem', sm: '1.5rem' }
                      }}
                    >
                      {getAllUsers().length}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      Łącznie Uczestników
                    </Typography>
                  </Box>

                  {/* Paid Count */}
                  <Box textAlign="center">
                    <PaymentIcon sx={{ color: '#4caf50', fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 0.5 }} />
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: '#4caf50',
                        fontSize: { xs: '1.3rem', sm: '1.5rem' }
                      }}
                    >
                      {lessonUsers.payed?.length || 0}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      Zapłacone
                    </Typography>
                  </Box>

                  {/* Attended Count */}
                  <Box textAlign="center">
                    <CheckCircleIcon sx={{ color: '#2196f3', fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 0.5 }} />
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: '#2196f3',
                        fontSize: { xs: '1.3rem', sm: '1.5rem' }
                      }}
                    >
                      {lessonUsers.attended?.length || 0}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      Obecni
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Filter Toggle - Improved with Icon + Text */}
          <Box sx={{ mb: 3, p: { xs: 1.5, sm: 2 }, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
            <ToggleButtonGroup
              value={filterBy}
              exclusive
              onChange={handleFilterChange}
              aria-label="filter by"
              size={isMobile ? "small" : "medium"}
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  borderRadius: 2,
                  px: { xs: 1.5, sm: 3 },
                  py: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 0.5, sm: 1 },
                }
              }}
            >
              <ToggleButton value="payment" aria-label="payment management">
                <PaymentIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.3rem' } }} />
                <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 500 }}>
                  Płatności
                </Typography>
              </ToggleButton>
              <ToggleButton value="attendance" aria-label="attendance list">
                <PeopleIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.3rem' } }} />
                <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, fontWeight: 500 }}>
                  Lista obecności
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Conditional rendering based on selected tab */}
          {filterBy === 'payment' ? (
            /* Payment Management View - Interactive with action buttons */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {lessonUsers && getAllUsers().map((user) => {
                const paymentStatus = getUserCurrentStatus(user, 'payment');
                const attendanceStatus = getUserCurrentStatus(user, 'attendance');
                
                return (
                  <Card 
                    key={`user-${user.id}`}
                    elevation={1}
                    sx={{
                      '&:hover': {
                        boxShadow: 3,
                      }
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: { xs: 'wrap', md: 'nowrap' }
                      }}>
                        {/* User Info */}
                        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 30%' } }}>
                          <Typography 
                            variant="subtitle1" 
                            fontWeight={600}
                            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                          >
                            {user.name}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                            {paymentStatus === 'paid' ? (
                              <>
                                <CheckCircleIcon sx={{ fontSize: '0.9rem', color: '#4caf50' }} />
                                <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 500 }}>
                                  {user.pass_id
                                    ? 'Zapisano karnetem'
                                    : user.paid_online ? 'Zapłacone online (PayU)' : 'Zapłacone'}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <CancelIcon sx={{ fontSize: '0.9rem', color: '#f44336' }} />
                                <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 500 }}>
                                  Nie zapłacone
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Box>

                        {/* Payment Button */}
                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 35%' } }}>
                          {user.pass_id || (paymentStatus === 'paid' && user.paid_online) ? (
                            // Paid with a pass or online: nothing to change here.
                            <Button
                              fullWidth
                              disabled
                              variant="contained"
                              size={isMobile ? "small" : "medium"}
                              startIcon={<InfoIcon />}
                              sx={{
                                backgroundColor: '#2196f3',
                                color: 'white',
                                opacity: 0.6,
                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                textTransform: 'none',
                                '&.Mui-disabled': {
                                  backgroundColor: '#2196f3',
                                  color: 'white',
                                }
                              }}
                            >
                              {user.pass_id
                                ? (isMobile ? 'Karnet' : 'Zapisano karnetem')
                                : (isMobile ? 'Online (PayU)' : 'Opłacone online (PayU)')}
                            </Button>
                          ) : paymentStatus === 'paid' ? (
                            // Undo is rare and creates debt: low-key button + confirmation.
                            <Button
                              fullWidth
                              variant="outlined"
                              color="warning"
                              size={isMobile ? "small" : "medium"}
                              startIcon={<CancelIcon />}
                              disabled={busyKey === `${user.id}:payment`}
                              onClick={() => setUndoPaymentUser(user)}
                              sx={{
                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                textTransform: 'none',
                              }}
                            >
                              Cofnij płatność
                            </Button>
                          ) : (
                            <Button
                              fullWidth
                              variant="contained"
                              size={isMobile ? "small" : "medium"}
                              startIcon={<PaymentIcon />}
                              disabled={busyKey === `${user.id}:payment`}
                              onClick={() => updateUserStatus(user.id, selectedLesson.lesson_id, 'payment', true)}
                              sx={{
                                backgroundColor: '#4caf50',
                                color: 'white',
                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: '#388e3c',
                                }
                              }}
                            >
                              Oznacz jako zapłacone
                            </Button>
                          )}
                        </Box>

                        {/* Attendance Button */}
                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 35%' } }}>
                          <Button
                            fullWidth
                            variant="contained"
                            size={isMobile ? "small" : "medium"}
                            startIcon={attendanceStatus === 'attended' ? <CancelIcon /> : <CheckCircleIcon />}
                            disabled={busyKey === `${user.id}:attendance`}
                            onClick={() => {
                              const newStatus = attendanceStatus !== 'attended';
                              updateUserStatus(user.id, selectedLesson.lesson_id, 'attendance', newStatus);
                            }}
                            sx={{
                              backgroundColor: attendanceStatus === 'attended' ? '#f44336' : '#2196f3',
                              color: 'white',
                              fontSize: { xs: '0.7rem', sm: '0.8rem' },
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: attendanceStatus === 'attended' ? '#d32f2f' : '#1976d2',
                              }
                            }}
                          >
                            {attendanceStatus === 'attended' ? 'Cofnij obecność' : 'Oznacz obecność'}
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            /* Attendance List View - Read-only, all users on one page */
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                      Uczestnik
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                      Status obecności
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                      Płatność
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lessonUsers && getAllUsers().map((user) => {
                    const paymentStatus = getUserCurrentStatus(user, 'payment');
                    const attendanceStatus = getUserCurrentStatus(user, 'attendance');
                    
                    return (
                      <TableRow 
                        key={`attendance-${user.id}`}
                        sx={{ 
                          '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                          '&:hover': { backgroundColor: '#f0f0f0' }
                        }}
                      >
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            fontWeight={500}
                            sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
                          >
                            {user.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={attendanceStatus === 'attended' ? <CheckCircleIcon /> : <CancelIcon />}
                            label={attendanceStatus === 'attended' ? 'Obecny' : 'Nieobecny'}
                            size="small"
                            sx={{
                              backgroundColor: attendanceStatus === 'attended' ? '#e8f5e9' : '#ffebee',
                              color: attendanceStatus === 'attended' ? '#2e7d32' : '#c62828',
                              fontWeight: 500,
                              fontSize: { xs: '0.7rem', sm: '0.8rem' },
                              '& .MuiChip-icon': {
                                color: attendanceStatus === 'attended' ? '#4caf50' : '#f44336',
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={paymentStatus === 'paid' ? <PaymentIcon /> : <CancelIcon />}
                            label={
                              user.pass_id 
                                ? 'Karnet' 
                                : (paymentStatus === 'paid' ? 'Zapłacone' : 'Nie zapłacone')
                            }
                            size="small"
                            sx={{
                              backgroundColor: paymentStatus === 'paid' ? '#e3f2fd' : '#fff3e0',
                              color: paymentStatus === 'paid' ? '#1565c0' : '#e65100',
                              fontWeight: 500,
                              fontSize: { xs: '0.7rem', sm: '0.8rem' },
                              '& .MuiChip-icon': {
                                color: paymentStatus === 'paid' ? '#2196f3' : '#ff9800',
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {/* Summary row for mobile - show total count */}
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    textAlign: 'center', 
                    color: 'text.secondary',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' }
                  }}
                >
                  Łączna liczba uczestników: <strong>{getAllUsers().length}</strong>
                </Typography>
              </Box>
            </TableContainer>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleCloseDialog} color="error" variant={isMobile ? "contained" : "text"}>
            Zamknij
          </Button>
        </Box>
      </Dialog>

      {/* Confirmation before undoing a payment */}
      <Dialog open={!!undoPaymentUser} onClose={() => setUndoPaymentUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cofnąć płatność?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Zajęcia osoby {undoPaymentUser?.name} zostaną oznaczone jako niezapłacone. Po zajęciach
            staną się zaległością (długiem) tej osoby i zablokują jej kolejne zapisy do czasu spłaty.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            Cofnij tylko wtedy, gdy płatność została oznaczona przez pomyłkę.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUndoPaymentUser(null)} autoFocus>
            Anuluj
          </Button>
          <Button onClick={confirmUndoPayment} color="warning" variant="contained">
            Cofnij płatność
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Payments;
